if (!customElements.get('custom-quiz-v2')) {
  customElements.define('custom-quiz-v2', class CustomQuizV2 extends HTMLElement {
    #loaderTimeoutId = null;
    #modalStepsResizeObserver = null;

    constructor() {
      super();
      this.errorDisplay = null;
      this.hasKidsCountStep = false;
      this.resultsGrid = null;
      this.resultSources = [];
      this.state = {
        currentStep: 1,
        kidsCount: 1,
        selectedAges: [],
        selectedInterests: [],
        selectedActivities: []
      };
    }

    connectedCallback() {
      this.errorDisplay = this.querySelector('[data-quiz-v2-error]');
      this.hasKidsCountStep = Boolean(this.querySelector('[data-type="kids-count"]'));
      this.resultsGrid = this.querySelector('[data-quiz-v2-results-grid]');
      this.resultSources = [...this.querySelectorAll('[data-quiz-v2-result-source]')];
      this.includeActivitiesInSubline = this.dataset.includeActivitiesInSubline === 'true';
      this.state.kidsCount = this.hasKidsCountStep ? 0 : 1;
      this.#initEvents();
      this.#syncBackButtons();
      this.#initModalStableStepHeight();
      this.#prefetchLoaderImage();
    }

    #prefetchLoaderImage() {
      const img = this.querySelector('.custom-quiz-v2__loader-image');

      if (img?.decode) {
        img.decode().catch(() => {});
      }
    }

    disconnectedCallback() {
      this.#clearResultsLoaderTimeout();
      this.#modalStepsResizeObserver?.disconnect();
      this.#modalStepsResizeObserver = null;
    }

    #initEvents() {
      this.addEventListener('click', (e) => {
        const t = e.target;

        if (t.closest?.('[data-quiz-v2-next]')) this.#moveStep(1);
        if (t.closest?.('[data-quiz-v2-prev]')) this.#moveStep(-1);
        if (t.closest?.('[data-quiz-v2-submit]')) this.#submit();

        const option = t.closest('[data-quiz-v2-option]');

        if (!option) return;

        const type = option.dataset.type;

        if (type === 'kids-count') {
          this.#selectKidsCount(option);
        } else if (type === 'age-selection') {
          this.#toggleAgeSelection(option);
        } else {
          this.#toggleDummySelection(option);
        }
      });

      this.addEventListener('keydown', (e) => {
        const option = e.target?.closest?.('[data-quiz-v2-option]');

        if (!option) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          option.click();
        }
      });
    }

    #selectKidsCount(card) {
      for (const option of this.querySelectorAll('[data-type="kids-count"]')) {
        option.classList.remove('is-selected');
        option.setAttribute('aria-checked', 'false');
      }

      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
      this.state.kidsCount = parseInt(card.dataset.value, 10) || 1;
      this.#syncAgeTitle();
      this.#hideError();
    }

    #syncAgeTitle() {
      const ageTitle = this.querySelector('[data-quiz-v2-age-title]');

      if (!ageTitle) return;

      const singular = ageTitle.dataset.titleSingular;
      const plural = ageTitle.dataset.titlePlural;

      if (!singular || !plural) return;

      ageTitle.textContent = this.state.kidsCount === 1 ? singular : plural;
    }

    #clearAgeSelection() {
      for (const option of this.querySelectorAll('[data-type="age-selection"]')) {
        option.classList.remove('is-selected');
        option.setAttribute('aria-checked', 'false');
      }
    }

    #toggleAgeSelection(card) {
      const val = parseInt(card.dataset.value, 10);
      const max = this.state.kidsCount || 1;
      const isRadio = max === 1 || !this.hasKidsCountStep;

      if (isRadio) {
        this.#clearAgeSelection();
        this.state.selectedAges = [val];
        card.classList.add('is-selected');
        card.setAttribute('aria-checked', 'true');
        this.#hideError();
        return;
      }

      const idx = this.state.selectedAges.indexOf(val);

      if (idx > -1) {
        this.state.selectedAges.splice(idx, 1);
        card.classList.remove('is-selected');
        card.setAttribute('aria-checked', 'false');
        this.#hideError();
        return;
      }

      if (this.state.selectedAges.length >= max) {
        const removedVal = this.state.selectedAges.shift();
        const removedCard = this.querySelector(`[data-type="age-selection"][data-value="${removedVal}"]`);

        if (removedCard) {
          removedCard.classList.remove('is-selected');
          removedCard.setAttribute('aria-checked', 'false');
        }
      }

      this.state.selectedAges.push(val);
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
      this.#hideError();
    }

    #toggleDummySelection(card) {
      card.classList.toggle('is-selected');
      card.setAttribute('aria-checked', card.classList.contains('is-selected'));

      if (card.dataset.type === 'interest-selection') {
        this.#syncInterestSelection(card);
      } else if (card.dataset.type === 'activity-selection') {
        this.#syncActivitySelection(card);
      }

      this.#hideError();
    }

    #syncActivitySelection(card) {
      const label = card.querySelector('.custom-quiz-v2__activity-label')?.textContent?.trim() || '';
      const idx = this.state.selectedActivities.indexOf(label);
      const isSelected = card.classList.contains('is-selected');

      if (isSelected && idx === -1 && label) {
        this.state.selectedActivities.push(label);
        return;
      }

      if (!isSelected && idx > -1) {
        this.state.selectedActivities.splice(idx, 1);
      }
    }

    #syncInterestSelection(card) {
      const label = card.querySelector('.custom-quiz-v2__option-label')?.textContent?.trim() || '';
      const idx = this.state.selectedInterests.indexOf(label);
      const isSelected = card.classList.contains('is-selected');

      if (isSelected && idx === -1 && label) {
        this.state.selectedInterests.push(label);
        return;
      }

      if (!isSelected && idx > -1) {
        this.state.selectedInterests.splice(idx, 1);
      }
    }

    #getSelectedAgeLabels() {
      return this.state.selectedAges
        .map((age) => {
          const card = this.querySelector(`[data-type="age-selection"][data-value="${age}"]`);

          return card?.querySelector('.custom-quiz-v2__option-label')?.textContent?.trim() || '';
        })
        .filter(Boolean);
    }

    #getInterestSummary() {
      return this.state.selectedInterests.join(' · ');
    }

    #getActivitySummary() {
      return this.state.selectedActivities.join(' · ');
    }

    #applyPersonalizationTemplate(template, ageText, interestText) {
      if (!template) return '';

      let personalized = template;

      if (interestText) {
        personalized = personalized.replace(/\[interest\]/gi, interestText);
      } else {
        personalized = personalized
          .replace(/,?\s*passend zu\s*\[interest\]/gi, '')
          .replace(/\[interest\]/gi, '');
      }

      if (ageText) {
        personalized = personalized.replace(/\[age\]/gi, ageText);
      } else {
        personalized = personalized
          .replace(/Ideal für\s*\[age\],?\s*/gi, '')
          .replace(/\[age\]/gi, '');
      }

      return personalized.replace(/\s+/g, ' ').trim();
    }

    #updateResultsPersonalization() {
      const ageText = this.#getSelectedAgeLabels().join(' · ');
      const interestText = this.#getInterestSummary();
      const sublineParts = [ageText, interestText].filter(Boolean);

      if (this.includeActivitiesInSubline) {
        const activityText = this.#getActivitySummary();

        if (activityText) {
          sublineParts.push(activityText);
        }
      }

      const sublineEl = this.querySelector('[data-quiz-v2-results-subline]');

      if (sublineEl) {
        sublineEl.textContent = sublineParts.join(' · ');
        sublineEl.hidden = sublineParts.length === 0;
      }

      if (!this.resultsGrid) return;

      for (const reasonEl of this.resultsGrid.querySelectorAll('[data-quiz-v2-result-reason]')) {
        const template = reasonEl.getAttribute('data-reason-template') || '';
        const reasonText = this.#applyPersonalizationTemplate(template, ageText, interestText);

        reasonEl.textContent = reasonText;
        reasonEl.hidden = !reasonText;
      }

      this.#positionResultReasons();
    }

    #positionResultReasons() {
      if (!this.resultsGrid) return;

      for (const resultItem of this.resultsGrid.querySelectorAll('.custom-quiz-v2__result-item')) {
        const reasonEl = resultItem.querySelector('[data-quiz-v2-result-reason]');
        const priceContainer = resultItem.querySelector('.product-item-meta__price-list-container');

        if (!reasonEl || !priceContainer) continue;

        priceContainer.insertAdjacentElement('afterend', reasonEl);
      }
    }

    #moveStep(dir) {
      if (dir > 0 && !this.#validate()) return;
      if (dir < 0 && this.state.currentStep <= this.#getMinQuizStepIndex()) return;

      this.state.currentStep += dir;
      this.#updateUI();
    }

    #prefersReducedMotion() {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    }

    #readCssPxNumber(el, prop) {
      const v = window.getComputedStyle(el).getPropertyValue(prop).trim();
      const n = parseFloat(v);

      return Number.isFinite(n) ? n : 0;
    }

    #scrollToSectionTop() {
      const section = this.closest('section.custom-quiz-v2');

      if (!section) return;

      const behavior = this.#prefersReducedMotion() ? 'auto' : 'smooth';
      const modalContent = this.closest('.modal__content');

      if (modalContent && typeof modalContent.scrollTo === 'function') {
        const modalRect = modalContent.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const top = modalContent.scrollTop + (sectionRect.top - modalRect.top);

        modalContent.scrollTo({ top: Math.max(0, top), behavior });
        return;
      }

      const root = document.documentElement;
      const headerOffset =
        this.#readCssPxNumber(root, '--header-height') * this.#readCssPxNumber(root, '--enable-sticky-header')
        + this.#readCssPxNumber(root, '--announcement-bar-height') * this.#readCssPxNumber(root, '--enable-sticky-announcement-bar');
      const targetTop = window.scrollY + section.getBoundingClientRect().top - headerOffset - 12;

      window.scrollTo({ top: Math.max(0, targetTop), behavior });
    }

    #getMinQuizStepIndex() {
      let minIdx = Infinity;

      for (const el of this.querySelectorAll('.custom-quiz-v2__step[data-step-index]')) {
        if (el.classList.contains('custom-quiz-v2__step--results') || el.classList.contains('custom-quiz-v2__step--loader')) continue;

        const n = parseInt(el.dataset.stepIndex, 10);

        if (!Number.isNaN(n)) minIdx = Math.min(minIdx, n);
      }

      return Number.isFinite(minIdx) ? minIdx : 1;
    }

    #validate() {
      this.#hideError();

      const currentStepEl = this.querySelector(`.custom-quiz-v2__step[data-step-index="${this.state.currentStep}"]`);

      if (!currentStepEl) return true;

      if (currentStepEl.classList.contains('custom-quiz-v2__step--interstitial') || currentStepEl.classList.contains('custom-quiz-v2__step--loader')) {
        return true;
      }

      if (currentStepEl.querySelector('[data-type="activity-selection"]') || currentStepEl.querySelector('[data-type="interest-selection"]')) {
        return true;
      }

      const errorMsg = this.getAttribute('data-error-text') || 'Please make a selection';

      if (currentStepEl.querySelector('[data-type="kids-count"]') && this.state.kidsCount === 0) {
        this.#showError(errorMsg);
        return false;
      }

      if (currentStepEl.querySelector('[data-type="age-selection"]') && this.state.selectedAges.length === 0) {
        this.#showError(errorMsg);
        return false;
      }

      return true;
    }

    #syncBackButtons() {
      const minStepIdx = this.#getMinQuizStepIndex();

      for (const btn of this.querySelectorAll('[data-quiz-v2-prev]')) {
        btn.hidden = this.state.currentStep <= minStepIdx;
      }
    }

    #updateUI() {
      const currentIdx = this.state.currentStep;

      for (const step of this.querySelectorAll('.custom-quiz-v2__step')) {
        const idx = parseInt(step.dataset.stepIndex, 10);

        step.classList.toggle('is-active', idx === currentIdx);
      }

      const stepEl = this.querySelector(`.custom-quiz-v2__step[data-step-index="${currentIdx}"]`);
      const progressStep = stepEl?.dataset.progressStep;

      for (const ind of this.querySelectorAll('.custom-quiz-v2__progress-step')) {
        const s = parseInt(ind.dataset.step, 10);

        if (progressStep === 'loader' || progressStep === 'results') {
          ind.classList.remove('is-active');
          ind.classList.add('is-completed');
          continue;
        }

        if (progressStep) {
          const p = parseInt(progressStep, 10);

          if (!Number.isNaN(p)) {
            ind.classList.toggle('is-active', s === p);
            ind.classList.toggle('is-completed', s < p);
          }

          continue;
        }

        ind.classList.toggle('is-active', s === currentIdx);
        ind.classList.toggle('is-completed', s < currentIdx);
      }

      this.#syncBackButtons();
      this.#hideError();

      if (progressStep === 'results') {
        this.#revealResultCards();
        this.#initReviewioRatingSnippets();
      }

      this.#syncModalStableStepHeight();
      this.#scrollToSectionTop();
    }

    #initReviewioRatingSnippets() {
      if (typeof window.ratingSnippet !== 'function') return;

      if (window.ratingSnippetGlobalSettings) {
        window.reinitialiseRatingSnippet();
        return;
      }

      const store = window.__REVIEWSIO_RATING_SNIPPET_STORE_OVERRIDE__ || window.reviewsIoStore || 'edurino.com';

      window.ratingSnippet('ruk_rating_snippet', {
        store,
        color: '#fb9100',
        textClr: '#415769',
        linebreak: false,
        usePolaris: true,
        text: 'Bewertungen',
        lang: window.reviewsIoLang || 'de',
        mode: 'default',
        showEmptyStars: false,
        enableSyndication: Boolean(window.reviewsIoEnableSyndication)
      });

      document.getElementById('reviews_fade')?.classList.add('rio-withoutBranding');
    }

    #initModalStableStepHeight() {
      const modal = this.closest('.custom-quiz-v2-modal');
      const form = this.querySelector('.custom-quiz-v2__form');

      if (!modal || !form) return;

      form.classList.add('custom-quiz-v2__form--modal-stable-height');
      this.#syncModalStableStepHeight();

      if (!this.#modalStepsResizeObserver) {
        this.#modalStepsResizeObserver = new ResizeObserver(() => {
          this.#syncModalStableStepHeight();
        });
        this.#modalStepsResizeObserver.observe(form);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.#syncModalStableStepHeight());
      }
    }

    #getModalMeasurableSteps() {
      const form = this.querySelector('.custom-quiz-v2__form');

      if (!form) return [];

      return [...form.querySelectorAll('.custom-quiz-v2__step')].filter(
        (step) => !step.classList.contains('custom-quiz-v2__step--results') && !step.classList.contains('custom-quiz-v2__step--loader')
      );
    }

    #syncModalStableStepHeight() {
      const form = this.querySelector('.custom-quiz-v2__form');

      if (!form?.classList.contains('custom-quiz-v2__form--modal-stable-height')) return;

      const currentStepEl = this.querySelector(`.custom-quiz-v2__step[data-step-index="${this.state.currentStep}"]`);

      if (
        currentStepEl?.classList.contains('custom-quiz-v2__step--results')
        || currentStepEl?.classList.contains('custom-quiz-v2__step--loader')
      ) {
        form.style.removeProperty('--quiz-modal-steps-min-height');
        return;
      }

      const steps = this.#getModalMeasurableSteps();

      if (!steps.length) return;

      form.classList.add('custom-quiz-v2__form--measuring');

      let maxHeight = 0;

      for (const step of steps) {
        maxHeight = Math.max(maxHeight, step.offsetHeight);
      }

      form.classList.remove('custom-quiz-v2__form--measuring');

      if (maxHeight > 0) {
        form.style.setProperty('--quiz-modal-steps-min-height', `${maxHeight}px`);
      }
    }

    #getSelectedAge() {
      return this.state.selectedAges[0];
    }

    #revealResultCards() {
      const age = Number(this.#getSelectedAge());
      const grid = this.resultsGrid;

      if (!grid) return;

      const fragment = document.createDocumentFragment();

      for (const source of this.resultSources) {
        const agesAttr = source.getAttribute('data-ages');

        if (agesAttr) {
          const ages = agesAttr.split(',').map((value) => parseInt(value, 10));

          if (!ages.includes(age)) continue;
        }

        fragment.append(source.content.cloneNode(true));
      }

      grid.replaceChildren(fragment);
      this.#updateResultsPersonalization();
    }

    #submit() {
      if (!this.#validate()) return;

      const loaderStep = this.querySelector('.custom-quiz-v2__step--loader');
      const resultStep = this.querySelector('.custom-quiz-v2__step--results');

      if (loaderStep) {
        this.state.currentStep = parseInt(loaderStep.dataset.stepIndex, 10);
        this.#updateUI();
        this.#scheduleResultsLoaderTransition();
        return;
      }

      if (resultStep) {
        this.state.currentStep = parseInt(resultStep.dataset.stepIndex, 10);
        this.#updateUI();
      }
    }

    #clearResultsLoaderTimeout() {
      if (this.#loaderTimeoutId != null) {
        clearTimeout(this.#loaderTimeoutId);
        this.#loaderTimeoutId = null;
      }
    }

    #scheduleResultsLoaderTransition() {
      this.#clearResultsLoaderTimeout();

      const loaderStep = this.querySelector('.custom-quiz-v2__step--loader');

      if (!loaderStep) return;

      const raw = loaderStep.dataset.loaderDurationMs;
      const parsed = raw ? parseInt(raw, 10) : 5000;
      const ms = Number.isNaN(parsed) || parsed < 0 ? 5000 : parsed;

      this.#loaderTimeoutId = setTimeout(() => {
        this.#loaderTimeoutId = null;

        const resultStep = this.querySelector('.custom-quiz-v2__step--results');

        if (resultStep) {
          this.state.currentStep = parseInt(resultStep.dataset.stepIndex, 10);
          this.#updateUI();
        }
      }, ms);
    }

    #showError(message) {
      if (!this.errorDisplay) return;

      this.errorDisplay.textContent = message;
      this.errorDisplay.classList.add('is-visible');
    }

    #hideError() {
      this.errorDisplay?.classList.remove('is-visible');
    }
  });
}